import { action, makeAutoObservable, observable, runInAction } from "mobx"

import notificationCenterService from "@services/notification/notificationCenterService"
import {
  NotificationPriority,
  NotificationReadStatus,
  PersistenceMode,
} from "@services/notification/dto/notificationCenterDto"
import type {
  GetMyNotificationsInput,
  GetSettingsInput,
  NotificationMessageDto,
  NotificationModule,
  NotificationSettingMatrixDto,
  UpdateNotificationSettingInput,
} from "@services/notification/dto/notificationCenterDto"

/** Bộ lọc hiển thị danh sách thông báo (read/module/priority) (R6.4). */
export interface NotificationCenterFilter {
  readStatus?: NotificationReadStatus
  module?: NotificationModule
  priority?: NotificationPriority
}

/**
 * Hình dạng tối thiểu của một thông báo realtime (đẩy qua SignalR) mà store cần để dựng một dòng
 * hiển thị trong danh sách (task 10.2). Cố ý khai báo cấu trúc (structural) tại đây để KHÔNG phụ
 * thuộc trực tiếp vào `signalRNotificationService` — tránh coupling vòng và giữ store thuần data.
 * `SignalRNotificationPayload` (id/eventCode/title/body/deepLink/createdAt) tương thích structural.
 */
export interface RealtimeNotificationInput {
  id: string
  eventCode: string
  title: string
  body: string
  deepLink: string
  createdAt: string
}

const DEFAULT_PAGE_SIZE = 20

/**
 * Giá trị enum backend `Module.APPROVAL` (SDJ.CRM.Core.Shared/Enums/ModuleEnum.cs) — dùng để suy ra
 * module cho một message realtime khi payload SignalR không mang sẵn `module`. EventCode approval có
 * tiền tố `Approval.` (vd `Approval.RequestSubmitted`) nên map về module Approval để lọc đúng (R8.4).
 */
const APPROVAL_MODULE_VALUE = 161
/** Tiền tố eventCode của các sự kiện approval (khớp `ApprovalNotificationEventCodes`). */
const APPROVAL_EVENTCODE_PREFIX = "Approval."

/**
 * Store MobX cho Notification Center trên React Portal. Giữ state danh sách thông báo
 * (đã sort giảm dần theo `createdTime` từ API — R6.3), số chưa đọc (R6.1), bộ lọc
 * (read/module/priority — R6.4) và cờ loading; gọi REST qua `notificationCenterService`.
 *
 * Tham chiếu: Requirements 6.1, 6.3, 6.4.
 */
class NotificationCenterStore {
  /** Danh sách thông báo hiển thị, giữ nguyên thứ tự giảm dần do API trả về (R6.3). */
  @observable notifications: NotificationMessageDto[] = []
  /** Tổng số bản ghi (phục vụ phân trang). */
  @observable totalCount = 0
  /** Số thông báo chưa đọc còn hiệu lực (R6.1). */
  @observable unreadCount = 0
  /** Bộ lọc hiện hành (read/module/priority) (R6.4). */
  @observable filter: NotificationCenterFilter = {}
  @observable isLoading = false

  /**
   * Ma trận cấu hình (Event × Channel) ở cấp/scope đang xem trên `NotificationSettingPage`
   * (R6.7/6.8). Mỗi ô mang cờ `mandatoryLocked` để UI hiển thị ON + khóa toggle (R6.8).
   */
  @observable settingMatrix: NotificationSettingMatrixDto | null = null
  /** Cờ loading riêng cho ma trận cấu hình, tách khỏi `isLoading` của danh sách. */
  @observable isSettingLoading = false

  constructor() {
    makeAutoObservable(this)
  }

  /** Cập nhật bộ lọc; merge từng phần để giữ các tiêu chí khác (R6.4). */
  @action
  public setFilter(filter: Partial<NotificationCenterFilter>) {
    this.filter = { ...this.filter, ...filter }
  }

  /** Xóa toàn bộ bộ lọc. */
  @action
  public resetFilter() {
    this.filter = {}
  }

  /** Nạp danh sách thông báo theo bộ lọc hiện hành (đã sort desc từ API — R6.3/6.4). */
  @action
  async loadNotifications(paging?: { maxResultCount?: number; skipCount?: number }) {
    this.isLoading = true
    const input: GetMyNotificationsInput = {
      readStatus: this.filter.readStatus,
      module: this.filter.module,
      priority: this.filter.priority,
      maxResultCount: paging?.maxResultCount ?? DEFAULT_PAGE_SIZE,
      skipCount: paging?.skipCount ?? 0,
    }
    try {
      const result = await notificationCenterService.getMyNotifications(input)
      runInAction(() => {
        this.notifications = result.items || []
        this.totalCount = result.totalCount || 0
      })
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  /** Nạp số thông báo chưa đọc còn hiệu lực (R6.1). */
  @action
  async loadUnreadCount() {
    const count = await notificationCenterService.getUnreadCount()
    runInAction(() => {
      this.unreadCount = count
    })
  }

  /**
   * Tăng số chưa đọc thêm `delta` (mặc định 1) khi nhận realtime một message mới qua SignalR,
   * cập nhật badge tức thời mà không cần reload (R6.2). Mỗi message mới tăng đúng một đơn vị.
   */
  @action
  public incrementUnread(delta = 1) {
    this.unreadCount += delta
  }

  /**
   * Đặt lại số chưa đọc về một giá trị tuyệt đối (không âm). Dùng khi cần đồng bộ badge với
   * con số mới nhất lấy từ server — vd sau khi SignalR reconnect (R8.6/R11.3), tránh lệch do
   * bỏ lỡ message lúc offline. Giữ nguồn duy nhất là store để badge và danh sách nhất quán.
   */
  @action
  public setUnread(count: number) {
    this.unreadCount = Math.max(0, Math.floor(count))
  }

  /**
   * Đánh dấu một thông báo đã đọc theo cơ chế optimistic + rollback (task 10.3 / R9.1, R9.3):
   * cập nhật ngay state cục bộ (readStatus/readTime) và giảm badge để UI phản hồi tức thì, sau đó
   * mới gọi API persist. Nếu API thất bại → hoàn tác về trạng thái trước đó và ném lại lỗi để scene
   * hiển thị thông báo lỗi. Nguồn duy nhất là store nên badge (observer) tự đồng bộ.
   */
  @action
  async markAsRead(id: string) {
    const target = this.notifications.find((x) => x.id === id)
    const wasUnread =
      !!target && target.readStatus !== NotificationReadStatus.Read
    const prevReadTime = target?.readTime
    const prevUnread = this.unreadCount

    // Optimistic: áp dụng trước khi gọi API.
    if (target) {
      target.readStatus = NotificationReadStatus.Read
      target.readTime = new Date().toISOString()
    }
    if (wasUnread && this.unreadCount > 0) {
      this.unreadCount -= 1
    }

    try {
      await notificationCenterService.markAsRead(id)
    } catch (error) {
      // Rollback khi persist thất bại.
      runInAction(() => {
        if (target) {
          target.readStatus = NotificationReadStatus.Unread
          target.readTime = prevReadTime
        }
        this.unreadCount = prevUnread
      })
      throw error
    }
  }

  /**
   * Đánh dấu tất cả đã đọc theo cơ chế optimistic + rollback (task 10.3 / R9.2, R9.3): xóa badge về
   * 0 và đánh dấu toàn bộ dòng đã đọc ngay lập tức, rồi gọi API. Nếu thất bại → khôi phục nguyên
   * trạng (readStatus/readTime từng dòng + badge) và ném lại lỗi. Idempotent.
   */
  @action
  async markAllAsRead() {
    // Snapshot để rollback nếu API lỗi.
    const snapshot = this.notifications.map((item) => ({
      item,
      readStatus: item.readStatus,
      readTime: item.readTime,
    }))
    const prevUnread = this.unreadCount

    // Optimistic.
    this.notifications.forEach((x) => {
      if (x.readStatus !== NotificationReadStatus.Read) {
        x.readStatus = NotificationReadStatus.Read
        x.readTime = new Date().toISOString()
      }
    })
    this.unreadCount = 0

    try {
      await notificationCenterService.markAllAsRead()
    } catch (error) {
      runInAction(() => {
        snapshot.forEach((s) => {
          s.item.readStatus = s.readStatus
          s.item.readTime = s.readTime
        })
        this.unreadCount = prevUnread
      })
      throw error
    }
  }

  /**
   * Thêm một thông báo realtime (nhận qua SignalR) lên đầu danh sách in-memory (task 10.2 / R8.2).
   *
   * QUAN TRỌNG — tránh đếm trùng (double-count): phương thức này CHỈ cập nhật DANH SÁCH, KHÔNG đụng
   * tới `unreadCount`. Việc tăng badge do `NotificationBadge` (task 10.1) đảm nhận khi cùng nhận
   * event SignalR. Scene (task 10.2) subscribe chỉ để prepend + toast; badge subscribe chỉ để đếm.
   * Nhờ vậy mỗi message mới chỉ tăng badge đúng một lần dù cả hai cùng mounted.
   *
   * Hành vi: bỏ qua nếu payload không có id; chống trùng theo id; và chỉ chèn nếu message khớp bộ
   * lọc hiện hành (readStatus/module/priority) để danh sách nhất quán với tiêu chí đang xem (R8.3/8.4).
   * Trả về `true` nếu đã chèn, `false` nếu bị bỏ qua (giúp scene quyết định có tăng totalCount hiển thị).
   */
  @action
  public prependRealtimeNotification(payload: RealtimeNotificationInput): boolean {
    if (!payload || !payload.id) return false
    // Chống trùng: message đã có trong danh sách thì không chèn lại.
    if (this.notifications.some((x) => x.id === payload.id)) return false

    const dto = this.mapRealtimeToMessage(payload)
    if (!this.matchesFilter(dto)) return false

    this.notifications.unshift(dto)
    this.totalCount += 1
    return true
  }

  /** Ánh xạ payload realtime → `NotificationMessageDto` tối thiểu đủ để render danh sách (R8.2). */
  private mapRealtimeToMessage(
    payload: RealtimeNotificationInput
  ): NotificationMessageDto {
    const isApproval = (payload.eventCode || "").startsWith(
      APPROVAL_EVENTCODE_PREFIX
    )
    return {
      id: payload.id,
      eventCode: payload.eventCode || "",
      // Dùng title làm nhãn hiển thị (category) khi backend chưa gửi category riêng.
      category: payload.title || payload.eventCode || "",
      priority: NotificationPriority.Normal,
      module: isApproval ? APPROVAL_MODULE_VALUE : 0,
      recipientUserId: 0,
      // Message realtime mới tới luôn là chưa đọc (R11.2).
      readStatus: NotificationReadStatus.Unread,
      createdTime: payload.createdAt || new Date().toISOString(),
      persistence: PersistenceMode.Persistent,
      deepLink: payload.deepLink || null,
      channels: [],
    }
  }

  /** Kiểm tra một message có khớp bộ lọc hiện hành hay không (R8.3/8.4). */
  private matchesFilter(dto: NotificationMessageDto): boolean {
    const { readStatus, module, priority } = this.filter
    if (readStatus !== undefined && dto.readStatus !== readStatus) return false
    if (module !== undefined && dto.module !== module) return false
    if (priority !== undefined && dto.priority !== priority) return false
    return true
  }

  /**
   * Nạp ma trận cấu hình ở một cấp/scope cho `NotificationSettingPage` (R6.7).
   * Backend trả về các ô đã phân giải (gồm `mandatoryLocked` để UI khóa toggle — R6.8).
   */
  @action
  async loadSettings(input: GetSettingsInput) {
    this.isSettingLoading = true
    try {
      this.settingMatrix = await notificationCenterService.getSettings(input)
    } finally {
      this.isSettingLoading = false
    }
  }

  /** Xóa ma trận cấu hình khỏi state (vd khi rời trang/đổi cấp). */
  @action
  public resetSettings() {
    this.settingMatrix = null
  }

  /**
   * Cập nhật một ô cấu hình (Event × Channel) ở một cấp/scope rồi nạp lại ma trận để phản ánh
   * giá trị đã phân giải (R4.8/4.10/6.8). Ô bị `mandatoryLocked` không được phép gọi (UI khóa).
   */
  @action
  async updateSetting(input: UpdateNotificationSettingInput) {
    await notificationCenterService.updateSetting(input)
    await this.loadSettings({
      level: input.level,
      projectId: input.projectId,
      userId: input.userId,
    })
  }
}

export default NotificationCenterStore
